import { useEffect, useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import { IconButton, Tooltip } from '@sweep-io/sweep-design';
import { AddMetadata } from './AddMetadata';
import { useDocumentationTabs } from './useDocumentationTabs';
import { useSelector, useDispatch } from 'react-redux';
import {
  DEFAULT_OBJECTS_TAB_ID,
  selectActiveDocumentationPageTab,
  selectPageTabs,
  setDocumentationPageType,
} from '../../../reducers/documentation/documentationPageReducer';
import TagIcon from './Tag.svg?react';
import { DocumentationPageType } from '../types';
import { getMetadataLabel } from './getMetadataLabel';
import { Close } from '@sweep-io/sweep-design/dist/icons/Close';
import { useDependencies } from '../dependencies/hooks/useDependencies';
import {
  DocumentationTabsLayout,
  DocumentationTab,
} from '../building-blocks/DocumentationTabsLayout';
import { useExpandedMode } from '../../pages/configuration-canvas/panels-reserve-space/useExpandedMode';

interface DocumentationTabsProps {
  children: React.ReactNode;
  documentationPageType: DocumentationPageType;
  crmOrgId: string;
}

export const DocumentationTabs = ({
  children,
  documentationPageType,
  crmOrgId,
}: DocumentationTabsProps) => {
  const pageTabs = useSelector(selectPageTabs);
  const tabs = useMemo(() => {
    return pageTabs?.[documentationPageType] ?? [];
  }, [pageTabs, documentationPageType]);

  const activeDocumentationPageTab = useSelector(selectActiveDocumentationPageTab);
  const { isExpandedMode } = useExpandedMode();
  const { onAddMetadata, onRemoveMetadata, setActiveTab } = useDocumentationTabs(crmOrgId);
  const { handleCloseDependencies } = useDependencies(crmOrgId);
  const selectedTypes = tabs.map((tab) => tab.id);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isExpandedMode && !activeDocumentationPageTab) {
      setActiveTab(DEFAULT_OBJECTS_TAB_ID);
    }
  }, [activeDocumentationPageTab, setActiveTab, isExpandedMode]);

  const renderTabs: DocumentationTab[] = useMemo(
    () =>
      tabs.map((tab) => ({
        id: tab.id,
        label: tab.label,
        isNotRemovable: tab._isNotRemovable,
      })),
    [tabs],
  );

  const handleTabChange = useCallback(
    (newValue: string) => {
      onAddMetadata(newValue, true);
      setActiveTab(newValue);
      handleCloseDependencies();
    },
    [onAddMetadata, setActiveTab, handleCloseDependencies],
  );

  const handleMount = useCallback(() => {
    handleCloseDependencies();
  }, [handleCloseDependencies]);

  const isMetadataTypes = documentationPageType === DocumentationPageType.metadataTypes;

  const renderTagIcon = () => {
    if (documentationPageType === DocumentationPageType.metadataTypes) {
      return (
        <Box sx={{ pr: 0.5 }} data-testid="tags-button-icon">
          <Tooltip title="Custom tags" placement="top">
            <span>
              <IconButton
                variant="system"
                size="small"
                onClick={() => {
                  dispatch(
                    setDocumentationPageType({
                      documentationPageType: DocumentationPageType.tagsAndUsers,
                    }),
                  );
                  if (isExpandedMode) {
                    setActiveTab('tags');
                  }
                }}
              >
                <TagIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      );
    }

    if (documentationPageType === DocumentationPageType.tagsAndUsers) {
      return (
        <Box sx={{ pr: 0.5 }}>
          <IconButton
            variant="flat"
            size="small"
            onClick={() => {
              dispatch(
                setDocumentationPageType({
                  documentationPageType: DocumentationPageType.metadataTypes,
                }),
              );
              setActiveTab(DEFAULT_OBJECTS_TAB_ID);
            }}
          >
            <Close />
          </IconButton>
        </Box>
      );
    }

    return null;
  };

  const renderActionSlot = isMetadataTypes
    ? (compact: boolean) => (
        <AddMetadata
          onAddMetadata={onAddMetadata}
          selectedTypes={selectedTypes}
          compact={compact}
          crmOrgId={crmOrgId}
        />
      )
    : undefined;

  const bottomSlot = isMetadataTypes ? (
    <>
      <AddMetadata
        onAddMetadata={onAddMetadata}
        selectedTypes={selectedTypes}
        crmOrgId={crmOrgId}
      />
      {renderTagIcon()}
    </>
  ) : undefined;

  return (
    <DocumentationTabsLayout
      tabs={renderTabs}
      activeTab={activeDocumentationPageTab}
      defaultTabId={DEFAULT_OBJECTS_TAB_ID}
      onTabChange={handleTabChange}
      onRemoveTab={onRemoveMetadata}
      formatLabel={getMetadataLabel}
      renderActionSlot={renderActionSlot}
      trailingSlot={renderTagIcon()}
      bottomSlot={bottomSlot}
      onMount={handleMount}
    >
      {children}
    </DocumentationTabsLayout>
  );
};
